import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Grid,
  MenuItem,
  IconButton,
  Typography,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
} from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useHistory } from 'react-router-dom';
import { InputField } from '../../components/InputController';
import { useStyles } from '../../style/Pages/LegalCompanyInfo';
import { useFormik } from 'formik';
import { schemaValidationBeneficialOwnerData, schemaValidationBeneficialOwnerDataType } from '../../utils/validation';
import { useDispatch, useSelector } from 'react-redux';
import { clearOnboardingApplicationFlags, selectOnboardingApplicationState, getUpdateOnboardingApplication, getPmDetails } from './sagas/pmonboardingSlice';
import { useParams } from 'react-router-dom';
import { UpdateBeneficialOfficerDTO } from '@inosio/payments-sdk';
import { format } from 'date-fns';
import { approvedISOStandards } from '../../shared/util/approvedISOStandards';
import CustomDropdown from '../../components/customDropdown';
import { removeDashes } from '../shared/util/helpers';
import BackIcon from '../../icons/backIcon.svg';
import { PhoneNumberInput } from '../../shared/PhoneNumberInput';
import { E164PhoneRegex } from '../../utils/regex';

interface Props {}
interface ParamTypes {
  id: string;
}

const enum PageStates {
  PersonalInfo = 'Personal Info',
  ContactInfo = 'Contact Info',
  Ownership = 'Ownership',
}

const initialVlaues: schemaValidationBeneficialOwnerDataType = {
  first_name: '',
  last_name: '',
  middle_name: '',
  ssn: '',
  email: '',
  phone: '',
  physical_address: '',
  city: '',
  state: approvedISOStandards.States[0].Code,
  zip: '',
  country: 'US',
  ownership_amount: 0,
  dob: '',
  passport_number: '',
};

export const BeneficialOwnerData = (props: Props) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { id } = useParams<ParamTypes>();

  const [savedValues, setSavedValues] = useState<schemaValidationBeneficialOwnerDataType>(initialVlaues);
  const [savedOwner, setSavedOwner] = useState<UpdateBeneficialOfficerDTO | undefined>(undefined);

  useEffect(() => {
    setPageState(PageStates.PersonalInfo);
    setSavedOwner(undefined);
    setAddBeneficialOwner('no'); // This is to reset form for further use. React doesn't update this state var instantly.
    formik && formik.setSubmitting(false);
    formik && formik.resetForm({ values: initialVlaues });
    formik && formik.validateForm();
  }, [id]);

  const [pageState, setPageState] = useState<PageStates | false>(PageStates.PersonalInfo);
  const handleAccordionChange = (panel: PageStates) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setPageState(isExpanded ? panel : false);
  };

  const { pmDetails, onboarding, isFetching, isSuccess } = useSelector(selectOnboardingApplicationState);

  React.useEffect(() => {
    // Clear all onboarding flags
    dispatch({
      type: clearOnboardingApplicationFlags.type,
      payload: {},
    });

    // fetch company uuid if onboarding state has been erased, probably due to a refresh
    !onboarding?.Uuid &&
      dispatch({
        type: getPmDetails.type,
        payload: {},
      });
  }, []);

  React.useEffect(() => {
    // fetch onboarding application if it doesnt exist in app context
    pmDetails.Company?.Uuid &&
      !onboarding?.Uuid &&
      dispatch({
        type: getUpdateOnboardingApplication.type,
        payload: {
          company_uuid: pmDetails.Company.Uuid,
          data: {},
        },
      });
  }, [pmDetails]);

  const [totalOwnership, setTotalOwnership] = useState(0);
  useEffect(() => {
    const ownership =
      (onboarding?.PrincipalOfficer?.OwnershipPercentage || 0) + (onboarding?.BeneficialOwners?.map((benOwner) => benOwner.OwnershipPercentage || 0).reduce((prev, next) => prev + next, 0) || 0);
    setTotalOwnership(ownership);
  }, [onboarding]);

  //Benificial Owner Data Saved Values
  useEffect(() => {
    if (onboarding?.BeneficialOwners?.[+id - 1]?.FirstName) {
      const owner = onboarding.BeneficialOwners[+id - 1];
      setSavedOwner(owner);
      setSavedValues({
        ...savedValues,
        first_name: owner.FirstName!,
        last_name: owner.LastName!,
        middle_name: owner.MiddleName || '',
        ssn: owner.Ssn!,
        email: owner.Email!,
        phone: owner.PhoneNumber!,
        physical_address: owner.PhysicalAddress?.Line1!,
        city: owner.PhysicalAddress?.City!,
        state: owner.PhysicalAddress?.State!,
        zip: owner.PhysicalAddress?.Zip!,
        country: owner.PhysicalAddress?.Country!,
        ownership_amount: owner.OwnershipPercentage!,
        dob: owner.Dob && format(new Date(owner.Dob), 'MM/dd/yyyy'),
        passport_number: owner.PassportIdNumber!,
      });
    }
  }, [onboarding, id]);

  const [isFormSubmitted, setIsFormSubmitted] = React.useState(false);
  const [addBeneficialOwner, setAddBeneficialOwner] = React.useState('no');
  const formik = useFormik<schemaValidationBeneficialOwnerDataType>({
    initialValues: savedValues,
    validationSchema: schemaValidationBeneficialOwnerData,
    onSubmit: async (values) => {
      dispatch({
        type: getUpdateOnboardingApplication.type,
        payload: {
          company_uuid: onboarding?.Uuid || pmDetails?.Company?.Uuid,
          data: {
            BeneficialOwners: [
              {
                Uuid: savedOwner?.Uuid,
                MiddleName: values.middle_name,
                Dob: format(new Date(values.dob), 'yyyy-MM-dd'), //expected: MM/dd/yyyy, converted to YYYY-MM-DD for api, // 11/04/1970
                PhysicalAddress: {
                  Uuid: savedOwner?.PhysicalAddress?.Uuid,
                  City: values.city,
                  State: values.state,
                  Zip: values.zip,
                  Line1: values.physical_address,
                  Country: values.country,
                },
                FirstName: values.first_name,
                LastName: values.last_name,
                Ssn: removeDashes(values.ssn),
                Email: values.email,
                PhoneNumber: removeDashes(values.phone),
                OwnershipPercentage: values.ownership_amount,
                PassportIdNumber: values.passport_number,
              },
            ],
          },
        },
      });
      setIsFormSubmitted(true);
    },
    enableReinitialize: true,
    validateOnMount: true,
  });

  React.useEffect(() => {
    if (isSuccess) {
      if (isFormSubmitted) {
        setIsFormSubmitted(false); //reset Form Data
        if (addBeneficialOwner === 'yes' && totalOwnership <= 75) {
          // we need to check ownership_amount here b.c. the minimum is 25 on the next step
          history.push(`/onboarding/beneficialownerdata/${+id + 1}`);
        } else {
          history.push(`/onboarding/applicationwizard`);
        }
      }

      // Clear all onboarding flags
      dispatch({
        type: clearOnboardingApplicationFlags.type,
        payload: {},
      });
    }
  }, [isFormSubmitted, isSuccess]);

  const [isActiveContactInfo, setIsActiveContactInfo] = useState(false);
  const [isActiveOwnership, setIsActiveOwnership] = useState(false);
  const [ownershipAmountError, setOwnershipAmountError] = useState('');
  const validateTotalOwnership = (ownershipValue: number) => +ownershipValue + +totalOwnership - +(savedOwner?.OwnershipPercentage || 0) <= +100;
  useEffect(() => {
    const { first_name, last_name, dob, ssn, passport_number, email, phone, physical_address, city, state, zip, country, ownership_amount } = formik.values;
    if (first_name && last_name && dob && ssn && ssn.match(/^\d{9}$/) && passport_number) {
      setIsActiveContactInfo(true);
    } else {
      setIsActiveContactInfo(false);
    }

    if (first_name && last_name && dob && ssn && ssn.match(/^\d{9}$/) && passport_number && email && phone && phone.match(E164PhoneRegex) && physical_address && city && state && zip && country) {
      setIsActiveOwnership(true);
    } else {
      setIsActiveOwnership(false);
    }

    if (!validateTotalOwnership(ownership_amount)) {
      setOwnershipAmountError('Total ownership must be less than 100%');
    } else {
      setOwnershipAmountError('');
    }
  }, [formik]);

  const { Countries: approvedCountries, States: approvedStates, Provinces: approvedProvinces } = approvedISOStandards;
  const [selectedCountry, setSelectedCountry] = useState('US');
  const [selectedState, setSelectedState] = useState(approvedStates[0].Code);
  const resetStateSelection = () => {
    formik.setFieldValue('state', '');
    formik.setFieldValue('state_of_incorporation', '');
    setSelectedState('');
  };

  // load selected state if a valid selection was made originally, else let plain text box render state of origin
  useEffect(() => {
    formik.values.country &&
      ['US', 'CA'].includes(formik.values.country) &&
      approvedStates.map((stateRecord) => stateRecord.Code).includes(formik.values.state) &&
      setSelectedState(formik.values.state);
  }, [onboarding, formik]);

  const handleClickFinishLater = () => {
    history.push('/onboarding/applicationwizard');
  };

  return (
    <Grid item xs={11} sm={9} lg={8} xl={7}>
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
        <IconButton sx={{ p: 1 }} onClick={handleClickFinishLater}>
          <img src={BackIcon} alt="cancel icon" width="17px" height="17px" />
        </IconButton>
        <Typography
          style={{
            fontFamily: 'Open Sans',
            fontWeight: 700,
            fontSize: '18px',
            lineHeight: '23px',
            color: '#14142B',
            margin: '18px 0',
          }}
          sx={{ p: 1 }}
        >
          Beneficial Owner Data
          <Tooltip
            title="This information is used by our underwriting and compliance department. It is important that the data matches what is publicly available in various government databases and filings. Inaccuracies can lead to requests for additional data and slow down  your approval."
            placement="right"
          >
            <HelpIcon sx={{ fontSize: 17, color: '#C4C4C4', cursor: 'pointer', ml: '5px' }} />
          </Tooltip>
        </Typography>
      </Box>

      <Box component="form" noValidate onSubmit={formik.handleSubmit} className={classes.centerLabel}>
        <Accordion classes={{ root: classes.accordionRoot }} sx={{ borderBottom: 'none' }} expanded={pageState === PageStates.PersonalInfo} onChange={handleAccordionChange(PageStates.PersonalInfo)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1bh-content" id="panel1bh-header">
            <Typography
              style={{
                fontWeight: 600,
                fontSize: '18px',
              }}
              className={classes.personalInfo}
            >
              Personal Information
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ display: 'flex', justifyContent: 'center', padding: '0 !important' }}>
            <Box sx={{ maxWidth: 340 }}>
              <Grid container spacing={0.5} direction="row">
                <Grid item xs={12} sm={6}>
                  <InputField
                    name={'first_name'}
                    label={'First name'}
                    isError={false}
                    errors={formik.errors?.first_name}
                    touched={formik.touched?.first_name}
                    onBlur={formik.handleBlur}
                    onChange={(e) => formik.setFieldValue('first_name', e.target.value)}
                    value={formik.values.first_name || ''}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputField
                    name={'middle_name'}
                    errors={formik.errors?.middle_name}
                    touched={formik.touched?.middle_name}
                    onBlur={formik.handleBlur}
                    onChange={(e) => formik.setFieldValue('middle_name', e.target.value)}
                    label={'Middle name'}
                    value={formik.values.middle_name || ''}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputField
                    name={'last_Name'}
                    errors={formik.errors?.last_name}
                    touched={formik.touched?.last_name}
                    onBlur={formik.handleBlur}
                    onChange={(e) => formik.setFieldValue('last_name', e.target.value)}
                    label={'Last name'}
                    value={formik.values.last_name || ''}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputField
                    name={'dob'}
                    errors={formik.errors?.dob}
                    touched={formik.touched?.dob}
                    onBlur={formik.handleBlur}
                    onChange={(e) => formik.setFieldValue('dob', e.target.value)}
                    label={'Date of Birth'}
                    placeholder={`MM/DD/YYYY`}
                    value={formik.values.dob || ''}
                  />
                </Grid>
              </Grid>
              <InputField
                name={'ssn'}
                isError={false}
                label={'SSN'}
                errors={formik.errors?.ssn}
                touched={formik.touched?.ssn}
                onBlur={formik.handleBlur}
                onChange={(e) => formik.setFieldValue('ssn', e.target.value)}
                value={formik.values.ssn || ''}
              />
              <InputField
                isError={false}
                label={'ID/Passport Number'}
                name={'passport_number'}
                errors={formik.errors?.passport_number}
                touched={formik.touched?.passport_number}
                onBlur={formik.handleBlur}
                onChange={(e) => formik.setFieldValue('passport_number', e.target.value)}
                value={formik.values.passport_number || ''}
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion
          disabled={!isActiveContactInfo}
          classes={{ root: classes.accordionRoot }}
          sx={{ borderBottom: 'none' }}
          expanded={pageState === PageStates.ContactInfo}
          onChange={handleAccordionChange(PageStates.ContactInfo)}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel2bh-content" id="panel2bh-header">
            <Typography
              style={{
                fontWeight: 600,
                fontSize: '18px',
              }}
              className={classes.personalInfo}
            >
              Contact Information
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ display: 'flex', justifyContent: 'center', padding: '0 !important' }}>
            <Box sx={{ maxWidth: 340 }}>
              <InputField
                name={'email'}
                isError={false}
                label={'Email'}
                errors={formik.errors?.email}
                touched={formik.touched?.email}
                onBlur={formik.handleBlur}
                value={formik.values.email || ''}
                onChange={(e) => formik.setFieldValue('email', e.target.value)}
              />
              <PhoneNumberInput
                {...formik.getFieldProps('phone')}
                label="Phone"
                showErrorMessage={!!formik.errors?.phone && !!formik.touched?.phone}
                onChange={(phone) => {
                  formik.setFieldValue('phone', phone.startsWith('+') ? phone : `+${phone}`);
                }}
                errorMessage={formik.errors?.phone}
              />
              {/*Adding margins to phoneinput above is making it wierd so adding it here, do not remove */}
              <Box sx={{ marginTop: '16px' }}></Box>
              <InputField
                name={'physical_address'}
                isError={false}
                label={'Physical Address'}
                errors={formik.errors?.physical_address}
                touched={formik.touched?.physical_address}
                onBlur={formik.handleBlur}
                value={formik.values.physical_address || ''}
                onChange={(e) => formik.setFieldValue('physical_address', e.target.value)}
              />
              <Grid container spacing={1} direction="row">
                <Grid item xs={12} sm={6}>
                  <InputField
                    name={'city'}
                    placeholder={'City'}
                    label={'City'}
                    isError={false}
                    errors={formik.errors?.city}
                    touched={formik.touched?.city}
                    onBlur={formik.handleBlur}
                    value={formik.values.city || ''}
                    onChange={(e) => formik.setFieldValue('city', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  {selectedCountry && ['US', 'CA'].includes(selectedCountry) ? (
                    <>
                      <CustomDropdown
                        isFullWidth
                        label={'State / Province'}
                        value={selectedState}
                        handleChange={(e) => {
                          setSelectedState(e.target.value);
                          formik.setFieldValue('state', e.target.value);
                        }}
                      >
                        {selectedCountry === 'US'
                          ? approvedStates.map((stateRecord) => (
                              <MenuItem style={{ height: '36px' }} key={stateRecord.Code} value={stateRecord.Code}>
                                {stateRecord.State + ' - ' + stateRecord.Code}
                              </MenuItem>
                            ))
                          : approvedProvinces.map((provinceRecord) => (
                              <MenuItem style={{ height: '36px' }} key={provinceRecord.Code} value={provinceRecord.Code}>
                                {provinceRecord.Province + ' - ' + provinceRecord.Code}
                              </MenuItem>
                            ))}{' '}
                      </CustomDropdown>
                    </>
                  ) : (
                    <InputField
                      value={formik.values.state || ''}
                      name={'state'}
                      isError={false}
                      errors={formik.errors?.state}
                      touched={formik.touched?.state}
                      onBlur={formik.handleBlur}
                      onChange={(e) => formik.setFieldValue('state', e.target.value)}
                      placeholder={'State / Province'}
                      label={'State / Province'}
                    />
                  )}
                </Grid>
              </Grid>
              <Grid container spacing={1} direction="row">
                <Grid item xs={12} sm={6}>
                  <InputField
                    name={'zip'}
                    placeholder={'Postal Code'}
                    label={'Postal Code'}
                    isError={false}
                    errors={formik.errors?.zip}
                    touched={formik.touched?.zip}
                    onBlur={formik.handleBlur}
                    value={formik.values.zip || ''}
                    onChange={(e) => formik.setFieldValue('zip', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomDropdown
                    label={'Country'}
                    isFullWidth
                    handleChange={(e: any) => {
                      resetStateSelection();

                      // update selected country
                      setSelectedCountry(e.target.value);
                      formik.setFieldValue('country', e.target.value);
                    }}
                    value={formik.values.country || ''}
                  >
                    {approvedCountries.map((countryRecord) => (
                      <MenuItem style={{ height: '36px' }} key={countryRecord.Code} value={countryRecord.Code}>
                        {countryRecord.Country + ' - ' + countryRecord.Code}
                      </MenuItem>
                    ))}
                  </CustomDropdown>
                </Grid>
              </Grid>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion disabled={!isActiveOwnership} classes={{ root: classes.accordionRoot }} expanded={pageState === PageStates.Ownership} onChange={handleAccordionChange(PageStates.Ownership)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel3bh-content" id="panel3bh-header">
            <Typography
              style={{
                fontWeight: 600,
                fontSize: '18px',
              }}
              className={classes.personalInfo}
            >
              Ownership
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ display: 'flex', justifyContent: 'center', padding: '0 !important' }}>
            <Box sx={{ maxWidth: 340, width: '100%', display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
              <Typography
                style={{
                  fontWeight: 400,
                  fontSize: '14px',
                  fontFamily: 'Work Sans',
                  fontStyle: 'normal',
                  lineHeight: '16px',
                  marginBottom: '24px',
                  maxWidth: 287,
                  textAlign: 'center',
                }}
              >
                What amount of the company does this person own? Must be 25 or greater.
              </Typography>

              <InputField
                isError={false}
                label={'Ownership Amount'}
                name={'ownership_amount'}
                errors={formik.errors?.ownership_amount || ownershipAmountError}
                touched={formik.touched?.ownership_amount}
                onBlur={formik.handleBlur}
                onChange={(e) => formik.setFieldValue('ownership_amount', parseInt(isNaN(parseInt(e.target?.value)) ? '0' : e.target?.value))}
                value={formik.values.ownership_amount || 0}
              />

              <Typography
                style={{
                  fontWeight: 400,
                  fontSize: '14px',
                  fontFamily: 'Work Sans',
                  fontStyle: 'normal',
                  lineHeight: '16px',
                  marginBottom: '24px',
                  maxWidth: 287,
                  textAlign: 'center',
                }}
              >
                Is there someone else who owns 25% or more of this company?
              </Typography>

              <FormControl sx={{ margin: '10px 0' }}>
                <RadioGroup row name="controlled-radio-buttons-group" value={addBeneficialOwner} onChange={(e) => setAddBeneficialOwner(e.target.value)}>
                  <FormControlLabel value="no" sx={{ marginRight: '30px' }} control={<Radio className={classes.radio} />} label="No" />
                  <FormControlLabel value="yes" control={<Radio className={classes.radio} />} label="Yes" />
                </RadioGroup>
              </FormControl>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Box style={{ width: '100%' }} sx={{ display: 'flex', justifyContent: 'end', marginTop: '26px' }}>
          <div className={classes.wrapper}>
            <Button type="submit" disabled={Boolean(!formik.isValid || !validateTotalOwnership(formik.values?.ownership_amount))} className={classes.continueBtn}>
              <Typography className={classes.continueBtnText} sx={{ margin: '0 10px' }}>
                Save
              </Typography>
            </Button>
            {isFormSubmitted && isFetching && <CircularProgress size={26} className={classes.buttonProgress} />}
          </div>
        </Box>
      </Box>
    </Grid>
  );
};
